import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ResetAllRedeemed } from '../../../../utilities/BL/Xtp';

const MASTER_PASSWORD_REQUIRED = 'evitarborrarregistros456852';

/**
 * MODAL: XtpResetRedeemedModal
 * Reversión Masiva de Canjes de Prueba a estado Pendiente.
 * Requiere clave maestra para garantizar que nadie reinicie los canjes por accidente o sin autorización.
 * NO borra participantes, tickets ni premios.
 */
export const XtpResetRedeemedModal = ({
    isOpen = false,
    onClose = () => {},
    onResetSuccess = () => {},
    showMessage = () => {}
}) => {
    const [passwordInput, setPasswordInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (isOpen) {
            setPasswordInput('');
            setErrorMessage('');
            setIsSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = async (e) => {
        if (e) e.preventDefault();

        if (passwordInput.trim() !== MASTER_PASSWORD_REQUIRED) {
            setErrorMessage('Contraseña incorrecta. No tienes autorización para reiniciar los canjes.');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');

        try {
            const res = await ResetAllRedeemed({
                password: passwordInput.trim(),
                operatorUser: 'Staff Admin'
            });

            if (res && res.success) {
                showMessage({
                    type: 'success',
                    message: res.message || 'Todos los canjes han sido restablecidos a estado Pendiente exitosamente.'
                });
                onResetSuccess();
                onClose();
            } else {
                setErrorMessage(res?.message || 'Error al ejecutar el reinicio de canjes en el servidor.');
            }
        } catch (error) {
            console.error('Error en XtpResetRedeemedModal:', error);
            setErrorMessage('Error de conexión con el servidor al procesar el reinicio de canjes.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1060 }}
        >
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                    {/* Header */}
                    <div className="modal-header bg-warning text-dark py-3 px-4 border-0">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2 mb-0">
                            <i className="bi bi-shield-lock-fill fs-4 text-dark"></i>
                            Reiniciar Canjes de Prueba a Pendiente
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Cerrar"
                            onClick={onClose}
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Body */}
                    <div className="modal-body p-4 bg-white text-dark">
                        {errorMessage && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" role="alert">
                                <i className="bi bi-exclamation-triangle-fill fs-5"></i>
                                <span className="small fw-semibold">{errorMessage}</span>
                            </div>
                        )}

                        <div className="p-3 bg-warning-subtle rounded-3 border border-warning border-opacity-50 mb-3">
                            <div className="d-flex align-items-start gap-2">
                                <i className="bi bi-info-circle-fill text-warning fs-4 flex-shrink-0 mt-0.5"></i>
                                <div>
                                    <h6 className="fw-bold mb-1 text-dark">¿Para qué sirve esta acción?</h6>
                                    <p className="small mb-1 text-dark">
                                        Restablecerá a estado <strong>PENDIENTE</strong> a todos los aficionados marcados como canjeados durante las pruebas.
                                    </p>
                                    <p className="small mb-0 text-success fw-bold">
                                        <i className="bi bi-check-circle-fill me-1"></i>
                                        GARANTÍA: Ningún aficionado, ticket, folio ni premio será eliminado. Solo se limpia el estado de entrega.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleConfirm}>
                            <div className="mb-3">
                                <label className="form-label small fw-bold text-secondary text-uppercase tracking-wider">
                                    Contraseña Maestra de Seguridad
                                </label>
                                <input
                                    type="password"
                                    className="form-control form-control-lg border-2"
                                    placeholder="Ingresa la contraseña maestra..."
                                    value={passwordInput}
                                    onChange={(e) => setPasswordInput(e.target.value)}
                                    autoFocus
                                    disabled={isSubmitting}
                                    autoComplete="off"
                                />
                                <div className="form-text small text-muted">
                                    Esta operación requiere autorización de nivel organizador para evitar reversiones no deseadas.
                                </div>
                            </div>

                            <div className="d-flex gap-2 justify-content-end mt-4">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary px-4 fw-semibold rounded-pill"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-warning px-4 fw-bold rounded-pill text-dark shadow-sm"
                                    disabled={isSubmitting || !passwordInput.trim()}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                            Restableciendo...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-counterclockwise me-1"></i>
                                            Confirmar y Reiniciar Canjes
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

XtpResetRedeemedModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onResetSuccess: PropTypes.func,
    showMessage: PropTypes.func
};
